import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { MotesPaths } from '@motes-sdk/models/motes-paths';
import { QrCreateBody, QrCreateResponse, QrLink, QrListResponse, QrUpdateBody } from '@motes-sdk/api-models/qr';
import { MotesLoginService } from '../motes-login';

@Injectable({ providedIn: 'root' })
export class QrService {
    private readonly baseUrl = `${MotesPaths.MOTES_REST}/v1/qr`;

    constructor(private http: HttpClient, private loginService: MotesLoginService) {}

    /**
     * Create a QR link scoped to a project.
     */
    public async create(body: QrCreateBody): Promise<QrCreateResponse> {
        this.assertRequired(body.targetUrl, 'targetUrl');
        this.assertRequired(body.ownerApp, 'ownerApp');
        this.assertRequired(body.ownerId, 'ownerId');
        this.assertRequired(body.project, 'project');
        const headers = await this.getAuthHeaders();
        return firstValueFrom(this.http.post<QrCreateResponse>(this.baseUrl, body, { headers }));
    }

    public async get(qrId: string): Promise<QrLink> {
        this.assertRequired(qrId, 'qrId');
        const headers = await this.getAuthHeaders();
        return firstValueFrom(this.http.get<QrLink>(`${this.baseUrl}/${qrId}`, { headers }));
    }

    public async update(qrId: string, body: QrUpdateBody): Promise<QrLink> {
        this.assertRequired(qrId, 'qrId');
        if(!body || Object.keys(body).length === 0) {
            throw { err: 'no_fields' };
        }
        const headers = await this.getAuthHeaders();
        return firstValueFrom(this.http.patch<QrLink>(`${this.baseUrl}/${qrId}`, body, { headers }));
    }

    public async remove(qrId: string): Promise<void> {
        this.assertRequired(qrId, 'qrId');
        const headers = await this.getAuthHeaders();
        await firstValueFrom(this.http.delete<void>(`${this.baseUrl}/${qrId}`, { headers }));
    }

    public async listByTarget(targetUrl: string, project: string): Promise<QrListResponse> {
        this.assertRequired(targetUrl, 'targetUrl');
        this.assertRequired(project, 'project');
        const headers = await this.getAuthHeaders();
        const params = new HttpParams().set('targetUrl', targetUrl).set('project', project);
        return firstValueFrom(this.http.get<QrListResponse>(`${this.baseUrl}/by-target`, { headers, params }));
    }

    public async listByProject(project: string): Promise<QrListResponse> {
        this.assertRequired(project, 'project');
        const headers = await this.getAuthHeaders();
        const params = new HttpParams().set('project', project);
        return firstValueFrom(this.http.get<QrListResponse>(`${this.baseUrl}/by-org`, { headers, params }));
    }

    public async listByOwner(ownerApp: QrLink['ownerApp'], ownerId: string, project: string): Promise<QrListResponse> {
        this.assertRequired(ownerApp, 'ownerApp');
        this.assertRequired(ownerId, 'ownerId');
        this.assertRequired(project, 'project');
        const headers = await this.getAuthHeaders();
        const params = new HttpParams()
            .set('ownerApp', ownerApp)
            .set('ownerId', ownerId)
            .set('project', project);
        return firstValueFrom(this.http.get<QrListResponse>(`${this.baseUrl}/by-owner`, { headers, params }));
    }

    private async getAuthHeaders(): Promise<HttpHeaders> {
        const token = await this.loginService.getIdToken();
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': token
        });
    }

    private assertRequired(value: any, field: string) {
        if(value === undefined || value === null || value === '') {
            throw { err: `${field} required` };
        }
    }
}
